import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { useCredits } from './useCredits';
import { formatNgn } from './insufficientCredits';
import { useAuthStore } from '../store/authStore';

/** Below this, topping up stops being optional soon — warn before it bites. */
const LOW_NGN = 200;

/**
 * Balance, always in view, one click from topping up.
 *
 * This is the whole discoverability story for payments: a user should never have
 * to go looking through settings to find out what they have left or to add more.
 * Hidden when signed out, and while billing is in shadow mode — showing a
 * balance that isn't being spent would be telling people something untrue.
 */
export const CreditChip: React.FC = () => {
  const isSignedIn = useAuthStore((s) => s.isAuthenticated);
  const { balance, enforced, openTopUp } = useCredits();

  if (!isSignedIn || !enforced || !balance) return null;

  const empty = balance.balanceNgn <= 0;
  const low = balance.balanceNgn < LOW_NGN;

  return (
    <Tooltip title={empty ? 'Out of credit — add more' : 'Credit balance. Click to top up.'}>
      <Chip
        size="small"
        onClick={openTopUp}
        icon={<AddIcon sx={{ fontSize: 16 }} />}
        label={formatNgn(balance.balanceNgn)}
        color={empty ? 'error' : low ? 'warning' : 'default'}
        variant={empty || low ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600, cursor: 'pointer' }}
        aria-label={`Credit balance ${formatNgn(balance.balanceNgn)}. Add credit.`}
      />
    </Tooltip>
  );
};
